import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { demoArticles } from './demo-articles';
import { demoVideos } from './demo-videos';
import WellnessCard from './wellness-card';

export const WellnessPage = () => {
  const navigate = useNavigate();
  useScrollToTop();

  const styles = {
    container: {
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    heroSection: {
      background: 'linear-gradient(135deg, #4285F4 0%, #5a9cf8 100%)',
      color: 'white',
      padding: '32px 20px',
      textAlign: 'center' as const
    },
    heroTitle: {
      fontSize: '28px',
      fontWeight: 'bold',
      marginBottom: '12px'
    },
    heroSubtitle: {
      fontSize: '16px',
      opacity: 0.9
    },
    content: {
      margin: '0 auto',
      width: '100%'
    },
    searchContainer: {
      position: 'relative' as const,
      marginBottom: '24px'
    },
    searchInput: {
      width: '100%',
      padding: '16px 48px 16px 16px',
      fontSize: '16px',
      border: '1px solid #e0e0e0',
      borderRadius: '12px',
      backgroundColor: 'white',
      outline: 'none',
      fontStyle: 'italic' as const,
      color: '#999'
    },
    searchIcon: {
      position: 'absolute' as const,
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '20px',
      height: '20px',
      color: '#999'
    },
    categoryTabs: {
      display: 'flex' as const,
      gap: '8px',
      marginBottom: '24px',
      overflowX: 'auto' as const,
      paddingBottom: '4px'
    },
    categoryTab: {
      padding: '8px 16px',
      fontSize: '14px',
      fontWeight: '500',
      borderRadius: '20px',
      border: 'none',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s ease'
    },
    categoryTabActive: {
      backgroundColor: '#4285F4',
      color: 'white'
    },
    categoryTabInactive: {
      backgroundColor: 'white',
      color: '#4285F4',
      border: '1px solid #e0e0e0'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333'
    },
    seeAllLink: {
      fontSize: '14px',
      color: '#4285F4',
      textDecoration: 'none',
      fontWeight: '500',
      cursor: 'pointer'
    },
    featuredSection: {
      marginBottom: '32px'
    },
    featuredGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px'
    },
    latestSection: {
      marginBottom: '32px'
    },
    latestGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px'
    },
    videoSection: {
      marginBottom: '32px'
    },
    videoGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px'
    },
    bottomSpacer: {
      height: '80px'
    }
  };

  const [ activeTab, setActiveTab ] = useState('All');

  const categories = [ 'All', 'Nutrition', 'Exercise', 'Mental Health', 'Sleep' ];

  const mapArticleToFeatured = (article: { id: string; title: string; summary: string; readTime: string }) => ({
    id: article.id,
    title: article.title,
    subtitle: article.summary.substring(0, 60) + '...',
    type: 'Article' as const,
    readTime: article.readTime,
    icon: 'M19,3H5C3.9,3 3,3.9 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M17,17H7V15H17V17M17,13H7V11H17V13M17,9H7V7H17V9Z'
  });

  const featuredArticles = demoArticles.slice(0, 2).map(mapArticleToFeatured);

  const mapArticleToLatest = (article: { id: string; title: string; readTime: string }) => ({
    id: article.id,
    title: article.title,
    type: 'Article' as const,
    readTime: article.readTime,
    image: '📖'
  });

  const latestArticles = demoArticles.slice(2, 6).map(mapArticleToLatest);

  const mapVideoToPopular = (video: { id: string; title: string; duration: string }) => ({
    id: video.id,
    title: video.title,
    type: 'Video' as const,
    readTime: video.duration,
    image: '▶️'
  });

  const popularVideos = demoVideos.slice(0, 4).map(mapVideoToPopular);

  const handleArticleClick = (articleId: string) => {
    navigate(`/wellness/articles/${articleId}`);
  };

  const handleVideoClick = (videoId: string) => {
    navigate(`/wellness/videos/${videoId}`);
  };

  const handleSeeAllArticles = () => {
    navigate('/wellness/articles');
  };

  const handleSeeAllVideos = () => {
    navigate('/wellness/videos');
  };

  const handleTabClick = (category: string) => {
    setActiveTab(category);
  };

  const handleFeaturedArticleClick = (articleId: string) => {
    handleArticleClick(articleId);
  };

  const handleLatestArticleClick = (articleId: string) => {
    handleArticleClick(articleId);
  };

  const handleVideoItemClick = (videoId: string) => {
    handleVideoClick(videoId);
  };

  const handleCategoryClick = (category: string) => {
    handleTabClick(category);
  };

  const handleFeaturedClick = (articleId: string) => {
    handleFeaturedArticleClick(articleId);
  };

  const handleLatestClick = (articleId: string) => {
    handleLatestArticleClick(articleId);
  };

  const handleVideoClickHandler = (videoId: string) => {
    handleVideoItemClick(videoId);
  };

  // Create bound handlers to avoid arrow functions in JSX
  const handleCategoryClickBound = (category: string) => () => handleCategoryClick(category);
  const handleFeaturedClickBound = (articleId: string) => () => handleFeaturedClick(articleId);
  const handleLatestClickBound = (articleId: string) => () => handleLatestClick(articleId);
  const handleVideoClickBound = (videoId: string) => () => handleVideoClickHandler(videoId);

  // Create map functions to avoid arrow functions in JSX
  const mapCategoryToButton = (category: string, index: number) => (
    <button
      key={index}
      style={{
        ...styles.categoryTab,
        ...(activeTab === category ? styles.categoryTabActive : styles.categoryTabInactive)
      }}
      onClick={handleCategoryClickBound(category)}
    >
      {category}
    </button>
  );

  const mapArticleToFeaturedCard = (article: { id: string; title: string; subtitle: string; type: 'Article'; readTime: string; icon: string }) => (
    <WellnessCard
      key={article.id}
      item={article}
      variant="featured"
      onClick={handleFeaturedClickBound(article.id)}
    />
  );

  const mapArticleToLatestCard = (article: { id: string; title: string; type: 'Article'; readTime: string; image: string }) => (
    <WellnessCard
      key={article.id}
      item={article}
      variant="latest"
      onClick={handleLatestClickBound(article.id)}
    />
  );

  const mapVideoToCard = (video: { id: string; title: string; type: 'Video'; readTime: string; image: string }) => (
    <WellnessCard
      key={video.id}
      item={video}
      variant="video"
      onClick={handleVideoClickBound(video.id)}
    />
  );

  // Create bound handlers for See All links
  const handleSeeAllArticlesBound = () => handleSeeAllArticles();
  const handleSeeAllVideosBound = () => handleSeeAllVideos();


  return (
    <div style={styles.container} className="wellness-page screen">
      {/* Hero Section */}
      <ScreenHeader
        title="Health & Wellness"
        description="Your journey to better health starts here"
      />

      <div style={styles.content}>
        {/* Search */}
        <div style={styles.searchContainer}>
          <input 
            style={styles.searchInput}
            placeholder="Search articles, videos, resources..."
          />
          <svg style={styles.searchIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
          </svg>
        </div>

        {/* Category Tabs */}
        <div style={styles.categoryTabs}>
          {categories.map(mapCategoryToButton)}
        </div>

        {/* Featured Articles */}
        <div style={styles.featuredSection}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Featured Articles</div>
            <span style={styles.seeAllLink} onClick={handleSeeAllArticlesBound}>See All</span>
          </div>
          
          <div style={styles.featuredGrid}>
            {featuredArticles.map(mapArticleToFeaturedCard)}
          </div>
        </div>

        {/* Latest Articles */}
        <div style={styles.latestSection}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Latest Articles</div>
            <span style={styles.seeAllLink} onClick={handleSeeAllArticlesBound}>See All</span>
          </div>
          
          <div style={styles.latestGrid}>
            {latestArticles.map(mapArticleToLatestCard)}
          </div>
        </div>

        {/* Popular Videos */}
        <div style={styles.videoSection}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Popular Videos</div>
            <span style={styles.seeAllLink} onClick={handleSeeAllVideosBound}>View Library</span>
          </div>
          
          <div style={styles.videoGrid}>
            {popularVideos.map(mapVideoToCard)}
          </div>
        </div>

        {/* Bottom spacing */}
        <div style={styles.bottomSpacer}></div>
      </div>
    </div>
  );
};

export default WellnessPage;